import { useCallback, useEffect, useRef, useState } from "react";
import {
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from "react-plaid-link";
import { toast } from "react-toastify";
import { postRequest } from "utils/httpClient.ts";
import {
  getPlaidItemsPath,
  getPlaidLinkEventsPath,
  getPlaidLinkTokenPath,
  getPlaidReconnectedPath,
} from "utils/backend-path-builders.ts";
import { useAppDispatch } from "state/hooks.ts";
import { fetchBankAccounts } from "state/slices/bank-accounts-slice.ts";
import { hideGlobalLoadingOverlay, showGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice.ts";
import { BankAccount } from "interfaces/commerce/bank-account.ts";

// Kept across the OAuth redirect so the return page can reopen the same Link session.
const PLAID_LINK_SESSION_KEY = "plaid_link_session";

export interface PlaidLinkSession {
  linkToken: string;
  returnUrl: string;
  reconnectAccountId?: string;
}

export const getStoredPlaidLinkSession = (): PlaidLinkSession | null => {
  try {
    const raw = sessionStorage.getItem(PLAID_LINK_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storePlaidLinkSession = (session: PlaidLinkSession) => {
  sessionStorage.setItem(PLAID_LINK_SESSION_KEY, JSON.stringify(session));
};

const clearPlaidLinkSession = () => {
  sessionStorage.removeItem(PLAID_LINK_SESSION_KEY);
};

type LinkCallback = "EVENT" | "EXIT" | "SUCCESS";

const recordLinkEvent = (callback: LinkCallback, eventName: string | null, metadata: object) => {
  postRequest(getPlaidLinkEventsPath(), {
    body: { callback, event_name: eventName, metadata },
    reportErrorToSentry: false,
  }).catch(() => {});
};

interface Params {
  returnUrl?: string;
  resumedSession?: PlaidLinkSession | null;
  onLinked?: (account: BankAccount) => void;
  onFinished?: () => void;
}

// Runs Plaid Link to add a bank, or to reconnect one in update mode.
export const usePlaidBankLink = ({ returnUrl = "/profile", resumedSession, onLinked, onFinished }: Params) => {
  const dispatch = useAppDispatch();
  const [linkToken, setLinkToken] = useState<string | null>(resumedSession?.linkToken ?? null);
  const [creatingToken, setCreatingToken] = useState(false);
  const reconnectAccountId = useRef<string | undefined>(resumedSession?.reconnectAccountId);

  const finish = useCallback(() => {
    clearPlaidLinkSession();
    setLinkToken(null);
    onFinished?.();
  }, [onFinished]);

  const onSuccess = useCallback(
    async (publicToken: string | null, metadata: PlaidLinkOnSuccessMetadata) => {
      recordLinkEvent("SUCCESS", null, metadata);
      const reconnectId = reconnectAccountId.current;
      const accountId = metadata.accounts[0]?.id;
      if (!reconnectId && (!publicToken || !accountId)) {
        toast.error("Failed to add bank account. Please try again.");
        finish();
        return;
      }

      dispatch(showGlobalLoadingOverlay({ message: reconnectId ? "Reconnecting your bank..." : "Adding your bank account..." }));
      try {
        const account: BankAccount = reconnectId
          ? await postRequest(getPlaidReconnectedPath(reconnectId), { body: {} })
          : await postRequest(getPlaidItemsPath(), {
              body: { public_token: publicToken, account_id: accountId },
            });
        await dispatch(fetchBankAccounts());
        onLinked?.(account);
      } catch (error: any) {
        toast.error(error?.response?.data?.friendly_error_message || "Failed to add bank account. Please try again.");
      } finally {
        dispatch(hideGlobalLoadingOverlay());
        finish();
      }
    },
    [dispatch, finish, onLinked]
  );

  const onExit = useCallback(
    (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      recordLinkEvent("EXIT", null, { ...metadata, error });
      finish();
    },
    [finish]
  );

  const onEvent = useCallback((eventName: string, metadata: PlaidLinkOnEventMetadata) => {
    recordLinkEvent("EVENT", eventName, metadata);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
    onEvent,
    receivedRedirectUri: resumedSession ? window.location.href : undefined,
  });

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // With an account id, Link opens in update mode for that account's bank.
  const startLink = useCallback(async (reconnectId?: string) => {
    setCreatingToken(true);
    reconnectAccountId.current = reconnectId;
    dispatch(showGlobalLoadingOverlay({ message: "Setting up bank account connection..." }));
    try {
      const { link_token } = await postRequest(getPlaidLinkTokenPath(), {
        body: reconnectId ? { bank_account_id: reconnectId } : {},
      });
      storePlaidLinkSession({ linkToken: link_token, returnUrl, reconnectAccountId: reconnectId });
      setLinkToken(link_token);
    } catch (error) {
      console.error("Error creating Plaid link token:", error);
      toast.error("Failed to set up bank account connection. Please try again.");
    } finally {
      setCreatingToken(false);
      dispatch(hideGlobalLoadingOverlay());
    }
  }, [dispatch, returnUrl]);

  return { startLink, linking: creatingToken || linkToken !== null };
};
